/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Avatar, Typography } from '@neo4j-ndl/react';
import classNames from 'classnames';

import type { CommonProps } from '../../_common/types';

type UserBubbleProps = {
  /** The content of the user bubble */
  children?: React.ReactNode;
  /** The props for the avatar */
  avatarProps?: React.ComponentProps<typeof Avatar>;
  /** The element to display after the content. Could be a CleanIconButton to edit the user bubble. */
  trailingElement?: React.ReactNode;
};

/**
 * The component is used to display the user's message in a chat.
 * It has an avatar to represent the user and a trailing element to display potential additional actions.
 *
 * @alpha - Changes to this component may be breaking.
 */
const UserBubbleComponent = ({
  avatarProps,
  trailingElement,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', UserBubbleProps>) => {
  const classes = classNames('ndl-ai-user-bubble', className);

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <Avatar
        type="image"
        {...avatarProps}
        size="small"
        shape="circle"
        className={classNames(
          'ndl-ai-user-bubble-avatar',
          avatarProps?.className,
        )}
      />
      <Typography variant="body-medium" className="ndl-ai-user-bubble-content">
        {children}
      </Typography>
      <div className="ndl-ai-user-bubble-trailing">{trailingElement}</div>
    </div>
  );
};

export { UserBubbleComponent as UserBubble };
